import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Platform,
  StyleSheet,
  TextInput,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import MaskInput from 'react-native-mask-input';

import Colors from '../../../theming/Colors';
import Spacing from '../../../theming/Spacing';
import { useThemeContext } from '../../../theming/ThemeContext';
import { colorForScheme } from '../../../theming/Theming';
import KitIcon from '../../KitIcon';
import KitText from '../../KitText';
import { Country } from '../constants/types';
import CountryCodeSelectorModal from './CountryCodeSelectorModal';

interface PhoneNumberInputProps {
  placeholder?: string;
  placeholderTextColor?: string;
  value?: string;
  label?: string;
  labelStyle?: TextStyle;
  onChangeText: (text: string) => void;
  onBlur?: () => void;
  onSubmitEditing?: () => void;
  style?: ViewStyle;
  inputStyle?: ViewStyle;
  autoFocus?: boolean;
  blurOnSubmit?: boolean;
  testID?: string;
  returnKeyType?: 'done' | 'go' | 'next' | 'search' | 'send';
  selectedCountry?: Country;
  onCountryChange?: (country: Country) => void;
  countries: Country[];
  subtext?: string;
}

// Utility function to convert expectedFormat string to mask array
export const createMaskFromFormat = (
  format: string
): Array<string | RegExp> => {
  if (!format) return [];

  return format.split('').map((char) => {
    if (char === 'X') {
      return /\d/;
    }

    return char;
  });
};

const KitInternationalPhoneNumberInput = ({
  placeholder,
  placeholderTextColor,
  value = '',
  label = '',
  labelStyle,
  onChangeText,
  onBlur,
  onSubmitEditing,
  style,
  inputStyle,
  autoFocus,
  blurOnSubmit,
  testID,
  returnKeyType,
  selectedCountry,
  onCountryChange,
  countries,
  subtext,
}: PhoneNumberInputProps): ReactElement => {
  // ------------------------------------------------------------
  // Context & Custom Hooks

  const { fontFamily } = useThemeContext();

  // ------------------------------------------------------------
  // Refs

  const phoneInputRef = useRef<TextInput>(null);

  // ------------------------------------------------------------
  // State

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // ------------------------------------------------------------
  // Computed Values

  const mask = createMaskFromFormat(
    selectedCountry?.format || 'XXXXXXXXXXXXXXX'
  );
  const effectivePlaceholder = selectedCountry?.format || placeholder;

  // ------------------------------------------------------------
  // Side Effects

  useEffect(() => {
    if (autoFocus) {
      if (Platform.OS === 'android') {
        setTimeout(() => {
          phoneInputRef.current?.focus();
        }, 150);
      } else {
        phoneInputRef.current?.focus();
      }
    }
  }, [autoFocus]);

  // ------------------------------------------------------------
  // Event Handlers & Callbacks

  const handleInputFocus = useCallback(() => setIsInputFocused(true), []);

  const handleInputBlur = useCallback(() => {
    onBlur?.();
    setIsInputFocused(false);
  }, [onBlur]);

  const openModal = useCallback(() => setIsModalVisible(true), []);

  const handleModalClose = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  const handleCountrySelect = useCallback(
    (country: Country): void => {
      onCountryChange?.(country);
      setIsModalVisible(false);
    },
    [onCountryChange]
  );

  return (
    <>
      <View style={style} testID={testID}>
        {label ? (
          <KitText
            color={Colors.N500}
            fontSize={14}
            semiBold
            style={[styles().label, labelStyle]}
          >
            {label}
          </KitText>
        ) : null}

        <View style={styles().container}>
          <TouchableOpacity
            testID='CountrySelector'
            style={styles(isInputFocused).countrySelector}
            onPress={openModal}
          >
            <View style={styles().countrySelectorContent}>
              {selectedCountry ? (
                <>
                  <KitText fontSize={20}>{selectedCountry?.flag}</KitText>
                  <KitText fontSize={16} color={Colors.N900}>
                    {selectedCountry?.callingCode}
                  </KitText>
                </>
              ) : (
                <KitText
                  fontSize={16}
                  color={Colors.N900}
                  style={{ paddingLeft: Spacing.l }}
                >
                  -
                </KitText>
              )}
            </View>
            <KitIcon name='arrow-down' size={20} color={Colors.N1000} />
          </TouchableOpacity>

          <View style={[styles(isInputFocused).inputContainer, inputStyle]}>
            <MaskInput
              ref={phoneInputRef}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              mask={mask}
              placeholder={effectivePlaceholder}
              placeholderTextColor={colorForScheme({
                default: placeholderTextColor || Colors.N500,
              })}
              keyboardType={Platform.OS === 'web' ? 'number-pad' : 'phone-pad'}
              returnKeyType={returnKeyType}
              blurOnSubmit={blurOnSubmit}
              value={value}
              style={[styles().maskInput, { fontFamily }]}
              onChangeText={onChangeText}
              onSubmitEditing={onSubmitEditing}
            />
          </View>
        </View>
        {subtext ? (
          <KitText color={Colors.N500} fontSize={14} style={styles().subtext}>
            {subtext}
          </KitText>
        ) : null}
      </View>

      <CountryCodeSelectorModal
        isVisible={isModalVisible}
        onClose={handleModalClose}
        countries={countries}
        selectedCountry={selectedCountry}
        onCountrySelect={handleCountrySelect}
      />
    </>
  );
};

const styles = (inputFocus?: boolean) =>
  StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: Spacing.m,
    },
    label: {
      lineHeight: 18,
      marginBottom: Spacing.s,
    },
    countrySelector: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: Spacing.l,
      backgroundColor: Colors.N50,
      borderRadius: 8,
      height: 44,
      paddingHorizontal: 14,
    },
    countrySelectorContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: Spacing.xs,
    },
    countryCodeInput: {
      width: 80,
    },
    countryCodeInputText: {
      paddingLeft: Spacing.xl,
    },
    inputContainer: {
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: Colors.N50,
      borderWidth: 2,
      borderRadius: 8,
      borderColor: inputFocus ? Colors.N900 : 'transparent',
      height: 44,
      paddingLeft: 14,
    },
    maskInput: {
      color: Colors.N900,
      fontSize: 17,
      lineHeight: 20,
      ...Platform.select({
        web: {
          outlineWidth: 0,
          outlineStyle: 'none' as 'solid' | 'dotted' | 'dashed' | undefined,
          fontFamily: 'inherit',
        },
      }),
      height: 44,
      width: '100%',
    },
    subtext: {
      marginTop: Spacing.m,
    },
  });

export default KitInternationalPhoneNumberInput;
